import { useState } from 'react';
import type { Address, Hash } from 'viem';
import { useWalletClient } from 'wagmi';

import type { TransactionQueueItem } from 'components';
import MAX_UINT256 from 'constants/maxUint256';
import { getContractAddress, isolatedPoolComptrollerAbi, positionRouterAbi } from 'libs/contracts';
import { stakingV2Abi } from 'libs/contracts/abis/bittensorPrecompiles';
import { wAlphaAbi, wsTAOAbi } from 'libs/contracts/abis/bittensorWrappers';
import { STAKING_V2_ADDRESS } from 'libs/contracts/config/bittensorPrecompiles';
import { wrapPositionRouterError } from 'libs/errors';
import { useAccountAddress, useChainId, usePublicClient } from 'libs/wallet';
import type { VToken } from 'types';

import {
  failedStepStateReducer,
  getIsRouterDelegated,
  getRouterQueueStep,
  getStakingAllowance,
  runRouterQueueStep,
} from '../shared/routerQueueCore';
import { invalidateRouterQueries } from '../shared/routerQueueInvalidation';
import {
  computeRouterDeadline,
  computeWrappedOutFloor,
  floorToRao,
} from '../shared/routerSlippage';
import type { BittensorNativeSupplySource } from './getBittensorSupplySourceTokens';

export interface RouterSupplyQueuePlanStep extends TransactionQueueItem {
  shouldSkip: boolean;
}

interface ExecuteRouterSupplyQueueInput {
  amountMantissa: bigint;
  nativeAlphaSource?: BittensorNativeSupplySource;
  slippagePercentage: number;
  vToken: VToken;
}

const getMinSharesOutFromUnderlying = ({
  exchangeRate,
  underlyingAmountMantissa,
  slippagePercentage,
}: {
  exchangeRate: bigint;
  underlyingAmountMantissa: bigint;
  slippagePercentage: number;
}) =>
  computeWrappedOutFloor({
    nativeAmount: underlyingAmountMantissa,
    exchangeRate,
    slippagePercentage,
  });

export const useRouterSupplyQueue = () => {
  const { accountAddress } = useAccountAddress();
  const { chainId } = useChainId();
  const { publicClient } = usePublicClient();
  const { data: walletClient } = useWalletClient({ account: accountAddress });
  const [items, setItems] = useState<TransactionQueueItem[]>([]);
  const [isRunning, setIsRunning] = useState(false);

  const updateItem = (id: string, status: TransactionQueueItem['status']) => {
    setItems((currentItems) =>
      currentItems.map((item) => (item.id === id ? { ...item, status } : item)),
    );
  };

  const runStep = ({
    action,
    ...step
  }: RouterSupplyQueuePlanStep & { action: () => Promise<Hash> }) =>
    runRouterQueueStep({ step, action, updateItem, publicClient });

  const execute = async ({
    amountMantissa,
    nativeAlphaSource,
    slippagePercentage,
    vToken,
  }: ExecuteRouterSupplyQueueInput) => {
    if (!accountAddress || !walletClient) {
      throw new Error('Wallet is not connected');
    }

    const routerAddress = getContractAddress({ name: 'PositionRouter', chainId });
    const poolComptrollerAddress = getContractAddress({
      name: 'LegacyPoolComptroller',
      chainId,
    });

    if (!routerAddress || !poolComptrollerAddress) {
      throw new Error('PositionRouter or LegacyPoolComptroller address not configured');
    }

    setItems([]);
    setIsRunning(true);

    try {
      const isNativeTAO = !nativeAlphaSource;
      const symbol = vToken.underlyingToken.symbol;

      const isDelegated = await getIsRouterDelegated({
        accountAddress,
        publicClient,
        poolComptrollerAddress: poolComptrollerAddress as Address,
        routerAddress: routerAddress as Address,
      });

      let stakingAllowance = 0n;
      if (nativeAlphaSource) {
        stakingAllowance = await getStakingAllowance({
          accountAddress,
          publicClient,
          routerAddress: routerAddress as Address,
          netuid: nativeAlphaSource.netuid,
        });
      }

      const plan: RouterSupplyQueuePlanStep[] = [
        {
          id: 'approve-delegate',
          label: 'Approve PositionRouter as delegate',
          shouldSkip: !!isDelegated,
          status: 'idle',
        },
        ...(nativeAlphaSource
          ? [
              {
                id: 'approve-native-alpha',
                label: 'Approve native alpha spend',
                shouldSkip: stakingAllowance >= amountMantissa,
                status: 'idle',
              } as RouterSupplyQueuePlanStep,
            ]
          : []),
        {
          id: 'router-supply',
          label: `Supply ${symbol}`,
          shouldSkip: false,
          status: 'idle',
        },
      ];
      setItems(plan);

      await runStep({
        ...getRouterQueueStep(plan, 'approve-delegate'),
        action: () =>
          walletClient.writeContract({
            address: poolComptrollerAddress as Address,
            abi: isolatedPoolComptrollerAbi,
            functionName: 'updateDelegate',
            args: [routerAddress as Address, true],
            account: accountAddress,
          }),
      });

      if (nativeAlphaSource) {
        await runStep({
          ...getRouterQueueStep(plan, 'approve-native-alpha'),
          action: () =>
            walletClient.writeContract({
              address: STAKING_V2_ADDRESS as Address,
              abi: stakingV2Abi,
              functionName: 'approve',
              args: [
                routerAddress as Address,
                nativeAlphaSource.netuid,
                BigInt(MAX_UINT256.toFixed()),
              ],
              account: accountAddress,
            }),
        });
      }

      const supplyStep = getRouterQueueStep(plan, 'router-supply');
      let exchangeRate: bigint;
      let effectiveAmount = amountMantissa;
      if (nativeAlphaSource) {
        // Read live at submit time — wAlpha rate updates every Subtensor block,
        // so a form-time cached rate would drift during the wallet-confirm window.
        exchangeRate = await publicClient.readContract({
          address: vToken.underlyingToken.address,
          abi: wAlphaAbi,
          functionName: 'exchangeRate',
        });
      } else {
        effectiveAmount = floorToRao(amountMantissa);
        if (effectiveAmount === 0n) {
          throw new Error('TAO supply amount must be at least 1 RAO (1e-9 TAO)');
        }
        exchangeRate = await publicClient.readContract({
          address: vToken.underlyingToken.address,
          abi: wsTAOAbi,
          functionName: 'exchangeRate',
        });
      }
      const minSharesOut = getMinSharesOutFromUnderlying({
        exchangeRate,
        underlyingAmountMantissa: effectiveAmount,
        slippagePercentage,
      });
      const deadline = computeRouterDeadline();

      await runStep({
        ...supplyStep,
        action: () =>
          isNativeTAO
            ? walletClient.writeContract({
                address: routerAddress as Address,
                abi: positionRouterAbi,
                functionName: 'supplyTAO',
                args: [vToken.address, minSharesOut, deadline],
                value: effectiveAmount,
                account: accountAddress,
              })
            : walletClient.writeContract({
                address: routerAddress as Address,
                abi: positionRouterAbi,
                functionName: 'supply',
                args: [vToken.address, amountMantissa, minSharesOut, deadline],
                account: accountAddress,
              }),
      });

      invalidateRouterQueries({
        accountAddress,
        chainId,
        poolComptrollerAddress: poolComptrollerAddress as Address,
        routerAddress: routerAddress as Address,
        vTokenAddresses: [vToken.address],
        tokenAddress: vToken.underlyingToken.address,
        netuid: nativeAlphaSource?.netuid,
      });
    } catch (error) {
      setItems(failedStepStateReducer);
      throw wrapPositionRouterError(error);
    } finally {
      setIsRunning(false);
    }
  };

  return { execute, isRunning, items };
};
